/**
 * Карта на дейността — one actor's reach, drawn.
 *
 * Seeded from a municipality, a person or a company. Each company's awards
 * in a municipality are collapsed into a single node carrying the count and
 * the total: a supplier with hundreds of contracts cannot be drawn contract
 * by contract, and "14 общини, 176m лв" is the shape a reader takes in.
 *
 * Every municipality carries the mayor in office when those awards were made
 * and the party that nominated them, which is what turns a map of contracts
 * into a map of proximity to an administration.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import cytoscape, { type ElementDefinition } from 'cytoscape';
import { graphql } from '../gql';
import { request } from '../lib/graphql';
import { useLocation } from '../lib/router';
import { attachTooltip } from '../lib/tooltip';
import { downloadGexf } from '../lib/gexf';
import { Amount, ErrorState, Loading, PageHead, SectionHead, Stat } from '../components/ui';
import { compact, moneyExact, num, truncate } from '../lib/format';

const ACTIVITY = graphql(`
  query ActivityMapFor($municipality: ID, $person: ID, $company: ID,
                       $minMunicipalities: Int!, $minValue: Float!) {
    activityMap(municipality: $municipality, person: $person, company: $company,
                minMunicipalities: $minMunicipalities, minValue: $minValue) {
      companies
      truncated
      blocks {
        uic name municipalityKey municipalityName province
        awards value mayor mayorHash party partyKey partyFamily
      }
      partners { uic name otherUic otherName shared value }
    }
  }
`);

const STYLE: Record<string, { colour: string; shape: string; size: number }> = {
  company: { colour: '#3987e5', shape: 'ellipse', size: 50 },
  partner: { colour: '#199e70', shape: 'hexagon', size: 40 },
  award: { colour: '#a8a29a', shape: 'round-rectangle', size: 34 },
  municipality: { colour: '#c9b8a0', shape: 'round-pentagon', size: 46 },
  person: { colour: '#d95926', shape: 'diamond', size: 36 },
  party: { colour: '#8f7fd0', shape: 'star', size: 40 },
};

const LEGEND: [string, string][] = [
  ['company', 'Фирма (в обхвата)'], ['partner', 'Съизпълнител'],
  ['award', 'Поръчки в общината (сбор)'], ['municipality', 'Община'],
  ['person', 'Кмет'], ['party', 'Партия'],
];

export function ActivityPage() {
  const { location } = useLocation();
  const [seedKind, setSeedKind] = useState<'municipality' | 'person' | 'company'>(
    location.params.get('person') ? 'person'
      : location.params.get('company') ? 'company' : 'municipality');
  const [seed, setSeed] = useState(
    location.params.get('municipality') ?? location.params.get('person')
    ?? location.params.get('company') ?? '');
  const [draft, setDraft] = useState(seed);
  const [minMunicipalities, setMinMunicipalities] = useState(2);
  const holder = useRef<HTMLDivElement>(null);

  const variables = {
    municipality: seedKind === 'municipality' && seed ? seed : null,
    person: seedKind === 'person' && seed ? seed : null,
    company: seedKind === 'company' && seed ? seed : null,
    minMunicipalities,
    minValue: 0,
  };

  const query = useQuery({
    queryKey: ['activity', variables],
    queryFn: () => request(ACTIVITY, variables),
    enabled: Boolean(seed),
    placeholderData: keepPreviousData,
  });
  const map = query.data?.activityMap;

  const elements = useMemo<ElementDefinition[]>(() => {
    if (!map) return [];
    const nodes = new Map<string, ElementDefinition>();
    const edges: ElementDefinition[] = [];
    const add = (id: string, type: string, label: string, href?: string,
                 details?: Record<string, string | number | null | undefined>) => {
      if (!nodes.has(id)) {
        nodes.set(id, { data: { id, type, label, href: href ?? '', details: details ?? {} } });
      }
      return id;
    };
    const link = (source: string, target: string, label: string) => {
      edges.push({ data: { id: `${source}>${target}:${label}`, source, target, label } });
    };

    for (const block of map.blocks) {
      const own = map.blocks.filter((other) => other.uic === block.uic);
      const company = add(`c:${block.uic}`, 'company',
        truncate(block.name ?? block.uic, 26), `#/companies/${block.uic}`, {
          'ЕИК': block.uic,
          'Общини в картата': own.length,
          'Общо в картата': moneyExact(own.reduce((sum, o) => sum + o.value, 0)),
          'Поръчки в картата': own.reduce((sum, o) => sum + o.awards, 0),
        });
      const municipality = add(`m:${block.municipalityKey}`, 'municipality',
        truncate(block.municipalityName ?? block.municipalityKey, 22),
        `#/municipalities/${encodeURIComponent(block.municipalityKey)}`, {
          'Област': block.province ?? '—',
          'Кмет': block.mayor ?? '—',
          'Партия': block.partyFamily ?? block.party ?? '—',
        });
      // the aggregate: every award this company won from this buyer, as one
      const award = add(`a:${block.uic}:${block.municipalityKey}`, 'award',
        `${block.awards} поръчки · ${compact(block.value)} лв`, undefined, {
          'Фирма': block.name ?? block.uic,
          'Община': block.municipalityName ?? block.municipalityKey,
          'Брой поръчки': block.awards,
          'Обща стойност': moneyExact(block.value),
          'Средно на поръчка': moneyExact(block.value / Math.max(1, block.awards)),
          'Кмет по време на възлагането': block.mayor ?? '—',
          'Партия': block.partyFamily ?? block.party ?? '—',
        });
      link(company, award, '');
      link(award, municipality, '');

      if (block.mayorHash && block.mayor) {
        const mayor = add(`p:${block.mayorHash}`, 'person',
          truncate(block.mayor, 24), `#/people/${block.mayorHash}`, {
            'Роля': `кмет на ${block.municipalityName ?? ''}`.trim(),
            'Партия': block.partyFamily ?? block.party ?? '—',
          });
        link(mayor, municipality, 'кмет');
        if (block.partyKey) {
          const party = add(`pa:${block.partyKey}`, 'party',
            truncate(block.partyFamily ?? block.party ?? '', 24),
            `#/parties/${encodeURIComponent(block.partyKey)}`, {
              'На бюлетината': block.party ?? '—',
            });
          link(mayor, party, 'издигнат от');
        }
      }
    }

    for (const partner of map.partners) {
      const own = `c:${partner.uic}`;
      if (!nodes.has(own)) continue;
      const other = add(`c:${partner.otherUic}`, 'partner',
        truncate(partner.otherName ?? partner.otherUic, 24),
        `#/companies/${partner.otherUic}`, {
          'ЕИК': partner.otherUic,
          'Общи поръчки с': partner.name ?? partner.uic,
          'Брой общи': partner.shared,
          'Стойност на общите': moneyExact(partner.value),
        });
      link(own, other, `${partner.shared} общи · ${compact(partner.value)} лв`);
    }
    return [...nodes.values(), ...edges];
  }, [map]);

  useEffect(() => {
    if (!holder.current || elements.length === 0) return undefined;
    const instance = cytoscape({
      container: holder.current,
      elements,
      wheelSensitivity: 0.25,
      style: [
        {
          selector: 'node',
          style: {
            label: 'data(label)',
            'background-color': (node: cytoscape.NodeSingular) =>
              STYLE[node.data('type') as string]?.colour ?? '#a8a29a',
            shape: ((node: cytoscape.NodeSingular) =>
              STYLE[node.data('type') as string]?.shape ?? 'ellipse') as never,
            width: ((node: cytoscape.NodeSingular) =>
              STYLE[node.data('type') as string]?.size ?? 40) as never,
            height: ((node: cytoscape.NodeSingular) =>
              (STYLE[node.data('type') as string]?.size ?? 40) * 0.78) as never,
            color: '#ece7dd',
            'font-size': 11,
            'font-family': 'IBM Plex Sans, sans-serif',
            'text-valign': 'bottom',
            'text-margin-y': 5,
            'text-wrap': 'wrap',
            'text-max-width': '130px',
            'text-outline-width': 3,
            'text-outline-color': '#050508',
            'border-width': 2,
            'border-color': '#050508',
          },
        },
        {
          selector: 'edge',
          style: {
            width: 1.6,
            'curve-style': 'bezier',
            'line-color': '#3a3a44',
            'target-arrow-shape': 'triangle',
            'target-arrow-color': '#3a3a44',
            'arrow-scale': 0.7,
            label: 'data(label)',
            'font-size': 9,
            'font-family': 'IBM Plex Mono, monospace',
            color: '#a19c92',
            'text-rotation': 'autorotate',
            'text-outline-width': 2,
            'text-outline-color': '#050508',
          },
        },
      ],
      layout: {
        name: 'cose', animate: false, padding: 40,
        nodeRepulsion: 450000, idealEdgeLength: 120, nodeOverlap: 24,
      } as never,
    });
    instance.fit(undefined, 40);
    const hideTooltip = attachTooltip(instance);
    instance.on('tap', 'node', (event) => {
      const href = event.target.data('href') as string;
      if (href) window.location.hash = href.replace(/^#/, '');
    });
    return () => { hideTooltip(); instance.destroy(); };
  }, [elements]);

  /* The same graph as a file, so it can be laid out, filtered and argued
     with somewhere other than here. */
  const exportGexf = () => {
    downloadGexf(
      `activity-${seed.replace(/[^0-9A-Za-zА-Яа-я]+/g, '-')}.gexf`,
      elements.filter((e) => !e.data['source']).map((e) => ({
        id: String(e.data['id']),
        label: String(e.data['label'] ?? ''),
        attributes: {
          type: String(e.data['type'] ?? ''),
          ...(e.data['details'] as Record<string, string | number> | undefined),
        },
      })),
      elements.filter((e) => e.data['source']).map((e) => ({
        source: String(e.data['source']),
        target: String(e.data['target']),
        label: String(e.data['label'] ?? ''),
      })),
      `Карта на дейността: ${seed}. Обхваща само общински възложители; `
      + 'поръчките на фирма в една община са събрани в един възел.',
    );
  };

  const totalValue = (map?.blocks ?? []).reduce((sum, b) => sum + b.value, 0);
  const municipalities = new Set((map?.blocks ?? []).map((b) => b.municipalityKey)).size;

  return (
    <>
      <PageHead
        eyebrow="Анализ"
        title="Карта на дейността"
        lede={'Тръгва от община, лице или фирма и следва връзките навън: в кои общини '
          + 'работи всяка фирма, колко е получила във всяка от тях, кой е управлявал '
          + 'общината тогава и кои други фирми са делили поръчка с нея.'}
      />

      <div className="facet-row">
        <label className="facet-label" htmlFor="seed-kind">Начало</label>
        <select className="facet-select" id="seed-kind" value={seedKind}
          onChange={(event) => setSeedKind(event.target.value as typeof seedKind)}>
          <option value="municipality">Община (ключ)</option>
          <option value="company">Фирма (ЕИК)</option>
          <option value="person">Лице (идентификатор)</option>
        </select>
        <input
          className="facet-input facet-input-grow"
          placeholder={seedKind === 'municipality' ? 'напр. ПЛОВДИВ|ПЛОВДИВ' : 'напр. 121706547'}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => { if (event.key === 'Enter') setSeed(draft.trim()); }}
        />
        <button type="button" className="btn" onClick={() => setSeed(draft.trim())}>
          Начертай
        </button>
        {elements.length > 0 && (
          <button type="button" className="btn" onClick={exportGexf}
            title="Изтегля графа като GEXF за Gephi">
            Изтегли за Gephi
          </button>
        )}
        <label className="facet-label" htmlFor="min-mun">Мин. общини на фирма</label>
        <select className="facet-select" id="min-mun" value={String(minMunicipalities)}
          onChange={(event) => setMinMunicipalities(Number(event.target.value))}>
          {[1, 2, 3, 5].map((n) => <option key={n} value={String(n)}>{n}</option>)}
        </select>
      </div>

      {!seed && (
        <p className="state">
          Изберете начална точка — или отворете картата от досие чрез бутона „Карта на
          дейността“.
        </p>
      )}
      {query.isError && <ErrorState message={(query.error).message} />}
      {seed && query.isPending && <Loading label="Сглобяване на картата…" />}
      {/* a seed that matches nothing returns null, and rendering nothing at
          all reads as a broken page rather than an empty answer */}
      {seed && !query.isPending && !map && (
        <p className="state">
          Няма намерена дейност за тази начална точка. Проверете ключа — общините се
          задават като „ОБЛАСТ|ОБЩИНА“, фирмите с ЕИК.
        </p>
      )}

      {map && (
        <>
          <div className="grid grid-4">
            <Stat value={num(map.companies)} label="Фирми в обхвата" />
            <Stat value={num(municipalities)} label="Общини" />
            <Stat value={<Amount value={totalValue} />} label="Обща стойност" />
            <Stat value={num(map.partners.length)} label="Съвместни поръчки" />
          </div>

          {map.truncated && (
            <p className="colophon-note">
              Показана е част от картината: за всяка фирма се чертаят най-големите ѝ общини
              и най-големите ѝ съизпълнители. Фирма, работеща в повече общини, отколкото са
              показани, е отбелязана с това ограничение — картата не е пълният ѝ обхват.
            </p>
          )}

          <div className="graph-shell">
            <div className="graph-legend">
              {LEGEND.map(([kind, label]) => (
                <span className="legend-item" key={kind}>
                  <span className="glyph" style={{ background: STYLE[kind]?.colour }} />
                  {label}
                </span>
              ))}
            </div>
            {elements.length > 0
              ? <div ref={holder} id="cy" />
              : <p className="state">Няма дейност за тази начална точка при тези условия.</p>}
          </div>

          <section className="block">
            <SectionHead title="Как се чете" aside="методология" />
            <dl className="detail-meta detail-meta-wide">
              <div>
                <dt>Сборен възел</dt>
                <dd>
                  Всички поръчки на една фирма в една община са събрани в един възел с брой
                  и обща стойност. Фирма с триста договора не може да се начертае договор по
                  договор, а „18 поръчки · 58 млн. лв“ се чете наведнъж.
                </dd>
              </div>
              <div>
                <dt>Кмет и партия</dt>
                <dd>
                  Кметът е този, чийто мандат покрива първата поръчка в сборния възел, а не
                  днешният. Партията е тази, издигнала го на съответните избори.
                </dd>
              </div>
              <div>
                <dt>Съизпълнител</dt>
                <dd>
                  Фирма, на която е възложена същата поръчка. Стойността по връзката е
                  стойността на общите поръчки — тя не е разпределена между изпълнителите,
                  защото източникът не публикува разпределение.
                </dd>
              </div>
              <div>
                <dt>Какво не показва</dt>
                <dd>
                  Само общински възложители. Министерствата се съхраняват като име върху
                  поръчката, а не като обект, затова дейност към тях не се чертае.
                </dd>
              </div>
            </dl>
          </section>
        </>
      )}
    </>
  );
}
